/* eslint no-cond-assign: 0 */
import {
    call,
    put,
    take,
    select
} from 'redux-saga/effects';

import {
    makeRequest
} from '@core/js/sagas/makeRequest';
import { pushModalTransition } from '@core/js/actions/modalActions';
import { init as initGenericModal } from '@core/js/components/genericModalContentsComponent';
import {
    reviewPagesRoute
} from '@core/js/utils/apiRoutes';

import {
    EXPAND_IMAGE,
    SET_REVIEW_PAGE,
    receiveReviews,
    saveProductError,
    updateReviewPages
} from '@product/js/actions/productActions';
import {
    warn
} from '@core/js/utils/logger';
import { updateProductReviewStats } from '@product/js/actions/reviewsActions';
import reviewsUtils from '@product/js/utils/reviewsUtils';
import productUtils from '@product/js/utils/utils';

import expandedImageTemplate from 'expanded-image.j2';

/**
 * Sets up API call to get data from BazaarVoice
 * @return {object}          The response from the BazaarVoice API call
 */
export function* getBazaarVoiceDataSaga() {
    const state = yield select();
    const product = productUtils.getProductForAction(state);

    let res = {};

    if (!state.core || !state.core.constants) {
        warn('Unable to get BazaarVoice data: Missing state.core.constants.');
        return res;
    }

    const limit = state.core.featureConfigs.browseDisplayOptions.reviewsPerPage || 8;
    const offset = reviewsUtils.getOffsetFromPage(product.productReviews.reviewPage, limit);
    const bvRoute = reviewPagesRoute(state, limit, offset);

    try {
        res = yield call(makeRequest, bvRoute);
    } catch (error) {
        warn('Unable to get BazaarVoice data:', error);
        yield put(saveProductError(error));
    }

    return res;
}

export function* updateProductReviewStatsWithBazaarVoiceData(bazaarVoiceReviewData, styleNumber) {
    const reviewStats = _.get(bazaarVoiceReviewData, `Includes.Products.${styleNumber}.ReviewStatistics`);

    if (reviewStats) {
        const fitSlideAverage = _.get(reviewStats, 'SecondaryRatingsAverages.fitSlide.AverageRating');
        const reviewStatFields = {
            AverageOverallRating: reviewStats.AverageOverallRating,
            TotalReviewCount: reviewStats.TotalReviewCount,
            averageFitRating: fitSlideAverage ? reviewsUtils.mapAverageFitValue(fitSlideAverage) : undefined
        };

        yield put(updateProductReviewStats(reviewStatFields));
    }
}

export function* setReviewPageSaga() {
    let action;

    while (action = yield take(SET_REVIEW_PAGE)) {
        const state = yield select();
        const product = productUtils.getProductForAction(state, action);

        const currentPage = parseInt(product.productReviews.reviewPage, 10);
        const limit = state.core.featureConfigs.browseDisplayOptions.reviewsPerPage || 8;
        const nextPage = {};
        const prevPage = {};

        // Update the query param to match the new review page
        reviewsUtils.updateReviewPageParam(currentPage);

        // Get new BazaarVoice data
        const res = yield call(getBazaarVoiceDataSaga);
        yield put(receiveReviews(res));

        // Update next and previous pages
        const totalPages = reviewsUtils.getPageTotalCount(res.TotalResults, limit);
        if (currentPage < totalPages) {
            const next = currentPage + 1;

            nextPage.qs = reviewsUtils.buildReviewPageQS(next);
            nextPage.page = next;
        }
        if (currentPage > 1) {
            const prev = currentPage - 1;

            prevPage.qs = reviewsUtils.buildReviewPageQS(prev);
            prevPage.page = prev;
        }

        yield put(updateReviewPages(totalPages, nextPage, prevPage));
        yield call(updateProductReviewStatsWithBazaarVoiceData, res, product.styleNumber);
    }
}

/**
 * Show expanded image in modal
 */
export function* expandImageSaga() {
    let action = {};

    while (action = yield take(EXPAND_IMAGE)) {
        const expandedInit = (store, overrides = {}) => {
            overrides.template = expandedImageTemplate;
            return initGenericModal(store, overrides);
        };

        const payload = pushModalTransition(expandedInit, {}, action.meta.event);

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    }
}

export default [
    setReviewPageSaga,
    expandImageSaga
];
